import React, { useState, useEffect, Fragment } from 'react'
import formatCPF from '@brazilian-utils/format-cpf'
import { onlyNumbers } from '@brazilian-utils/helper-only-numbers'
import { format, startOfMonth } from 'date-fns'
import Select from 'react-select'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'

import RelatorioService from '../../services/relatorio.service'
import PageLoading from '../Common/PageLoading'
import { Table, Button, Spinner, Row, Col, OverlayTrigger, Tooltip, Card, Form, Modal } from 'react-bootstrap'
import Paginator from '../Common/Paginator'
import CSVDownload from './CSVDownload'
import colaboradoresService from '../../services/colaboradores.service'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { formatMoney } from '../../utils/string.utils'
import { statusPlanoTratamento } from '../../constants/common.constants'
import SystemConstants from '../../constants/system.constants'

const obterTelefoneContato = (grupo) => {
  const whatsApp = grupo.telefonesWhatsApp?.split(',')[0]?.trim()
  if (whatsApp) return whatsApp
  return grupo.telefonePrincipal || '-'
}

const obterStatusTratamento = (status) => {
  const item = statusPlanoTratamento.find((s) => s.value === status)
  return item ? item.label : '-'
}

const possuiTratamentoVinculado = (grupo) =>
  grupo?.numeroPlano || (grupo?.planoTratamentoId && grupo.planoTratamentoId !== '00000000-0000-0000-0000-000000000000')

export default function CobrancaJuridica() {
  const itensPorPagina = 20
  const [isLoading, setLoading] = useState(false)
  const [isLoadingCSV, setLoadingCSV] = useState(false)
  const [gruposLista, setGruposLista] = useState([])
  const [feedbackBusca, setFeedbackBusca] = useState('')
  const [pagina, setPagina] = useState(0)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [itensCSV, setItensCSV] = useState([])
  const [downloadCSV, setDownloadCSV] = useState(false)
  const [selectedEspecialidade, setSelectedEspecialidade] = useState(null)
  const [selectedComParceiroFinanceiro, setSelectedComParceiroFinanceiro] = useState(null)
  const [selectedTemContrato, setSelectedTemContrato] = useState(null)
  const [especialidadeOptions, setEspecialidadeOptions] = useState([])
  const [comParceiroFinanceiroOptions, setComParceiroFinanceiroOptions] = useState([])
  const [temContratoOptions, setTemContratoOptions] = useState([])
  const [resumo, setResumo] = useState([])
  const [dateFilterAte, setDateFilterAte] = useState(new Date())
  const [dateFilterDe, setDateFilterDe] = useState(startOfMonth(new Date()))
  const [cpfFilter, setCpfFilter] = useState('')
  const [filterLoading, setFilterLoading] = useState(false)
  const [showModalDetalhes, setShowModalDetalhes] = useState(false)
  const [detalheSelecionado, setDetalheSelecionado] = useState(null)

  const user = JSON.parse(localStorage.getItem(SystemConstants.getUser()))
  const isAdmin = user?.podeAlternarConta

  const obterTotalPacientes = () => {
    const item = resumo?.find((r) => r.descricao === 'Total de pacientes')
    if (!item?.valor) return 0
    const total = parseInt(item.valor, 10)
    return Number.isNaN(total) ? 0 : total
  }

  const totalPacientes = obterTotalPacientes()
  const indiceInicial = totalPacientes === 0 ? 0 : pagina * itensPorPagina + 1
  const indiceFinal = Math.min((pagina + 1) * itensPorPagina, totalPacientes)

  const adicionarFiltroCpf = (filtros) => {
    const cpf = onlyNumbers(cpfFilter || '')
    if (cpf) {
      filtros.push({ campo: 'cpf', valor: cpf })
    }
  }

  const montarFiltros = () => {
    const filtros = []

    if (selectedEspecialidade) {
      filtros.push({ campo: 'especialidadeId', valor: selectedEspecialidade.value })
    }

    dateFilterDe && filtros.push({ campo: 'dataInicial', valor: dateFilterDe })
    dateFilterAte && filtros.push({ campo: 'dataFinal', valor: dateFilterAte })

    if (selectedComParceiroFinanceiro && selectedComParceiroFinanceiro.value !== '-1') {
      filtros.push({ campo: 'comParceiroFinanceiro', valor: selectedComParceiroFinanceiro.value })
    }

    if (selectedTemContrato && selectedTemContrato.value !== '-1') {
      filtros.push({ campo: 'temContrato', valor: selectedTemContrato.value })
    }

    filtros.push({ campo: 'tipoRelatorio', valor: 'negativados' })
    filtros.push({ campo: 'marcado', valor: '1' })
    adicionarFiltroCpf(filtros)

    return filtros
  }

  useEffect(() => {
    didMount()
  }, [])

  useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina])

  useEffect(() => {
    if (downloadCSV) {
      setTimeout(() => {
        setDownloadCSV(false)
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itensCSV, downloadCSV])

  const didMount = async () => {
    setFilterLoading(true)

    const { data } = await colaboradoresService.getAllEspecialidades()
    const especialidades = [{ label: 'Todos', value: '00000000-0000-0000-0000-000000000000' }]

    data.map(({ nome, id }) => especialidades.push({
      label: nome,
      value: id,
    }))

    setEspecialidadeOptions(especialidades)
    setSelectedEspecialidade({ label: 'Todos', value: '00000000-0000-0000-0000-000000000000' })

    setComParceiroFinanceiroOptions([
      { value: '-1', label: 'Todos' },
      { value: '1', label: 'Sim' },
      { value: '0', label: 'Não' },
    ])
    setSelectedComParceiroFinanceiro({ label: 'Todos', value: '-1' })

    setTemContratoOptions([
      { value: '-1', label: 'Todos' },
      { value: '1', label: 'Sim' },
      { value: '0', label: 'Não' },
    ])
    setSelectedTemContrato({ label: 'Todos', value: '-1' })

    handleSearch()
    setFilterLoading(false)
  }

  const getValuesLista = async () => {
    setLoadingCSV(true)
    const { lista, resumo: resumoCsv } = await RelatorioService.getRegistrosNegativadosAgrupado(montarFiltros(), 1, 999999)

    const dados = lista.dados
    setResumo(resumoCsv)

    const valores = dados.map((grupo) => [
      grupo.nome,
      formatCPF(grupo.cpf),
      obterTelefoneContato(grupo),
      grupo.dataCriacao ? format(new Date(grupo.dataCriacao), 'dd/MM/yyyy') : '-',
      grupo.dataNegativado ? format(new Date(grupo.dataNegativado), 'dd/MM/yyyy') : '-',
      grupo.colaboradorNegativacao || '-',
      grupo.comParceiroFinanceiro ? 'Sim' : 'Não',
      grupo.temContrato ? 'Sim' : 'Não',
    ])

    setItensCSV(valores)
    setDownloadCSV(true)
    setLoadingCSV(false)
    return {
      data: valores,
      headers: [
        'Nome',
        'CPF',
        'Telefone',
        'Data Entrada',
        'Data Negativado',
        'Responsavel Operação',
        'Processo Jurídico',
        'Tem Contrato',
      ],
      filename: `Cobrança Jurídica - ${format(new Date(), 'dd-MM-yyyy')}.csv`,
    }
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      setFeedbackBusca('')
      const { lista, resumo: resumoBusca } = await RelatorioService.getRegistrosNegativadosAgrupado(
        montarFiltros(),
        pagina + 1,
        itensPorPagina
      )

      const dados = lista.dados

      setGruposLista(dados)
      setTotalPaginas(lista.totalPaginas)
      setResumo(resumoBusca)

      if (dados.length === 0) {
        setFeedbackBusca('Nenhum registro encontrado')
      }
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const handleClear = () => {
    setPagina(0)
    setDateFilterDe(startOfMonth(new Date()))
    setDateFilterAte(new Date())
    setSelectedEspecialidade({ label: 'Todos', value: '00000000-0000-0000-0000-000000000000' })
    setSelectedComParceiroFinanceiro({ label: 'Todos', value: '-1' })
    setSelectedTemContrato({ label: 'Todos', value: '-1' })
    setCpfFilter('')
  }

  const handlePagina = async (data) => setPagina(data.selected)

  const handlePesquisar = () => {
    if (pagina !== 0) {
      setPagina(0)
      return
    }
    handleSearch()
  }

  const handleDateFilterDe = (date) => {
    setDateFilterDe(date ? date.toDate() : null)
    setPagina(0)
  }

  const handleDateFilterAte = (date) => {
    setDateFilterAte(date ? date.toDate() : null)
    setPagina(0)
  }

  const abrirDetalhes = (grupo) => {
    setDetalheSelecionado(grupo)
    setShowModalDetalhes(true)
  }

  const fecharDetalhes = () => {
    setShowModalDetalhes(false)
    setDetalheSelecionado(null)
  }

  const renderModalDetalhes = () => {
    if (!detalheSelecionado) return null

    const grupo = detalheSelecionado
    const temTratamento = possuiTratamentoVinculado(grupo)

    return (
      <Modal show={showModalDetalhes} onHide={fecharDetalhes} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da cobrança jurídica</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3 text-muted small">
            Tratamento vinculado à negativação confirmada de{' '}
            <strong>{grupo.nome?.toUpperCase()}</strong> — {formatCPF(grupo.cpf)}
          </p>

          {temTratamento ? (
            <Table bordered size="sm" className="mb-0">
              <tbody>
                <tr>
                  <th width="40%">Tipo de tratamento</th>
                  <td>{grupo.tipoPlano === 1 ? 'Ortodontia' : 'Clínico geral'}</td>
                </tr>
                <tr>
                  <th>Nº do plano</th>
                  <td>{grupo.numeroPlano || '-'}</td>
                </tr>
                <tr>
                  <th>Status do tratamento</th>
                  <td>{obterStatusTratamento(grupo.statusTratamento)}</td>
                </tr>
                <tr>
                  <th>Parcela</th>
                  <td>{grupo.parcela || '-'}</td>
                </tr>
                <tr>
                  <th>Vencimento</th>
                  <td>{grupo.dataVencimento ? format(new Date(grupo.dataVencimento), 'dd/MM/yyyy') : '-'}</td>
                </tr>
                <tr>
                  <th>Valor da parcela</th>
                  <td>{grupo.valorParcela ? formatMoney(grupo.valorParcela) : '-'}</td>
                </tr>
                <tr>
                  <th>Processo jurídico</th>
                  <td>{grupo.comParceiroFinanceiro ? 'Sim' : 'Não'}</td>
                </tr>
                <tr>
                  <th>Tem contrato</th>
                  <td>{grupo.temContrato ? 'Sim' : 'Não'}</td>
                </tr>
              </tbody>
            </Table>
          ) : (
            <p className="mb-0 text-muted">Nenhum tratamento vinculado a este registro.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {grupo.pacienteId && (
            <Button
              variant="outline-primary"
              href={`/pacientes/${grupo.pacienteId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir paciente
            </Button>
          )}
          <Button variant="secondary" onClick={fecharDetalhes}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  const onChangeProcessoJuridico = async (grupo, e) => {
    if (!e.target.checked && !isAdmin) {
      Swal.fire({
        title: 'Operação não autorizada',
        text: 'Não é possível realizar essa operação. Por favor, entre em contato com administrador do sistema.',
        type: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Ok',
      })
      return
    }

    const checked = !e.target.checked
    const mensagem = !checked
      ? 'Ao marcar essa opção você está confirmando que já foi realizada a operação junto ao Jurídico. Deseja confirmar?'
      : 'Ao desmarcar essa opção você está confirmando que a operação não foi realizada junto ao Jurídico. Deseja confirmar?'

    Swal.fire({
      title: 'Confirmar Operação',
      text: mensagem,
      type: 'warning',
      showCancelButton: true,
      cancelButtonColor: '#dbdbdb',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Confirmar',
    }).then(async (result) => {
      if (result.value === true) {
        await RelatorioService.atualizaCobrancaJuridicaComParceiroFinanceiro({
          idHistoricoNegativacao: grupo.idHistoricoNegativacao,
          comParceiroFinanceiro: !checked,
        })

        Swal.fire('Confirmado', 'Operação confirmada com sucesso.', 'success').then(async () => {
          await handleSearch()
        })
      }
    })
  }

  const renderDatePicker = (label, value, onChange, { minDate, maxDate, placeholder }) => (
    <Form.Group className="mb-0">
      <Form.Label>{label}</Form.Label>
      <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
        <KeyboardDatePicker
          invalidDateMessage="Formato de data inválido"
          value={value}
          onChange={onChange}
          className="form-control w-100"
          variant="inline"
          format="DD/MM/YYYY"
          minDate={minDate}
          maxDate={maxDate}
          animateYearScrolling
          autoOk
          placeholder={placeholder}
        />
      </MuiPickersUtilsProvider>
    </Form.Group>
  )

  const renderItens = () =>
    gruposLista.length > 0 ? (
      <Fragment>
        <div className="mt-4 d-flex justify-content-end align-items-center">
          <CSVDownload asyncExportMethod={getValuesLista}>
            <Button variant="outline-secondary" className="mb-3">
              {isLoadingCSV ? <Spinner animation="border" size="sm" variant="light" /> : 'Gerar CSV'}
            </Button>
          </CSVDownload>
        </div>

        <Card className="mb-4 py-2">
          <Row>
            {resumo?.map(({ descricao, valor }, index) => (
              <Col key={index} className="d-flex align-items-center justify-content-center flex-column">
                <small className="text-center text-muted m-0">{descricao ? descricao.toUpperCase() : '-'}</small>
                <h4 className="text-center m-0 font-weight-bold">{valor || '-'}</h4>
              </Col>
            ))}
          </Row>
        </Card>

        <Table hover size="sm" className="border rounded-0 shadow-sm" style={{ backgroundColor: 'white' }}>
          <thead>
            <tr>
              <th width="180px">Nome</th>
              <th width="100px" className="text-center">CPF</th>
              <th width="130px" className="text-left">Telefone</th>
              <th width="90px" className="text-center">Data Entrada</th>
              <th width="90px" className="text-center">Negativado</th>
              <th width="120px" className="text-center">Responsável</th>
              <th width="90px" className="text-center">Processo Jurídico</th>
              <th width="90px" className="text-center">Detalhes</th>
            </tr>
          </thead>
          <tbody>
            {gruposLista.map((grupo, gi) => (
              <tr
                key={grupo.cpf || gi}
                style={{
                  fontSize: 'smaller',
                  backgroundColor: grupo.temContrato ? '#fff3cd' : 'white',
                }}
              >
                <td>
                  {grupo.pacienteId ? (
                    <a
                      href={`/pacientes/${grupo.pacienteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontWeight: '500' }}
                      onMouseEnter={(e) => { e.target.style.textDecoration = 'underline' }}
                      onMouseLeave={(e) => { e.target.style.textDecoration = 'none' }}
                      title="Abrir cadastro do paciente em nova aba"
                    >
                      {grupo.nome?.toUpperCase()}{' '}
                      <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" style={{ marginLeft: '5px', opacity: 0.7 }} />
                    </a>
                  ) : (
                    grupo.nome?.toUpperCase()
                  )}
                </td>
                <td className="text-center">{formatCPF(grupo.cpf)}</td>
                <td className="text-left">{obterTelefoneContato(grupo)}</td>
                <td className="text-center">
                  {grupo.dataCriacao ? format(new Date(grupo.dataCriacao), 'dd/MM/yyyy') : '-'}
                </td>
                <td className="text-center">
                  {grupo.dataNegativado ? format(new Date(grupo.dataNegativado), 'dd/MM/yyyy') : '-'}
                </td>
                <td className="text-center">{grupo.colaboradorNegativacao || '-'}</td>
                <td className="text-center">
                  <OverlayTrigger
                    trigger="hover"
                    placement="top"
                    overlay={
                      <Tooltip>
                        {grupo.comParceiroFinanceiro ? 'Retirar o processo jurídico' : 'Iniciar processo jurídico'}
                      </Tooltip>
                    }
                  >
                    <label className="checkbox labeless m-0">
                      <input
                        type="checkbox"
                        checked={grupo.comParceiroFinanceiro}
                        onChange={(e) => onChangeProcessoJuridico(grupo, e)}
                      />
                      <span />
                    </label>
                  </OverlayTrigger>
                </td>
                <td className="text-center">
                  <Button variant="outline-secondary" size="sm" onClick={() => abrirDetalhes(grupo)}>
                    Detalhes
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {totalPaginas > 1 && (
          <p className="text-muted small text-center mb-2">
            Exibindo {indiceInicial}–{indiceFinal} de {totalPacientes} pacientes
          </p>
        )}
        {totalPaginas > 1 && <Paginator handlePagina={handlePagina} pageCount={totalPaginas} paginaAtual={pagina} />}
        {renderModalDetalhes()}
      </Fragment>
    ) : (
      <div className="mt-4">{feedbackBusca}</div>
    )

  const renderLoading = () => <PageLoading />

  return (
    <Fragment>
      <h4 className="mb-3">Cobrança Jurídica</h4>
      <p className="text-muted small mb-3">
        Pacientes com negativação confirmada no SPC (negativado = Sim), agrupados por CPF.
      </p>

      <Row className="mb-2 align-items-end">
        <Col md={4} lg={3}>
          <Form.Group className="mb-0">
            <Form.Label>Especialidade</Form.Label>
            <Select
              placeholder="Especialidade"
              className="w-100"
              onChange={setSelectedEspecialidade}
              value={selectedEspecialidade}
              options={especialidadeOptions}
            />
          </Form.Group>
        </Col>
        <Col md={4} lg={3}>
          <Form.Group className="mb-0">
            <Form.Label>CPF</Form.Label>
            <Form.Control
              type="text"
              placeholder="000.000.000-00"
              value={cpfFilter}
              onChange={(e) => {
                setCpfFilter(e.target.value)
                setPagina(0)
              }}
            />
          </Form.Group>
        </Col>
        <Col md={4} lg={3}>
          <Form.Group className="mb-0">
            <Form.Label>Com Parceiro Financeiro</Form.Label>
            <Select
              placeholder="Com Parceiro Financeiro"
              className="w-100"
              onChange={setSelectedComParceiroFinanceiro}
              value={selectedComParceiroFinanceiro}
              options={comParceiroFinanceiroOptions}
            />
          </Form.Group>
        </Col>
        <Col md={4} lg={3}>
          <Form.Group className="mb-0">
            <Form.Label>Tem Contrato</Form.Label>
            <Select
              placeholder="Tem Contrato"
              className="w-100"
              onChange={setSelectedTemContrato}
              value={selectedTemContrato}
              options={temContratoOptions}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 align-items-end">
        <Col md={4} lg={3}>
          {renderDatePicker('Data Inicial', dateFilterDe, handleDateFilterDe, {
            minDate: moment(dateFilterAte).add(-90, 'days'),
            maxDate: dateFilterAte || moment(),
            placeholder: 'De',
          })}
        </Col>
        <Col md={4} lg={3}>
          {renderDatePicker('Data Final', dateFilterAte, handleDateFilterAte, {
            minDate: dateFilterDe || moment(),
            maxDate: moment(),
            placeholder: 'Até',
          })}
        </Col>
      </Row>

      <Row className="mb-2">
        <Col className="text-right">
          {filterLoading && <Spinner animation="border" variant="primary" size="sm" style={spinnerStyle} />}
          <Button disabled={filterLoading} onClick={handleClear} variant="outline-danger" className="mr-2">
            Limpar
          </Button>
          <Button disabled={filterLoading} onClick={handlePesquisar} variant="primary">
            Pesquisar
          </Button>
        </Col>
      </Row>

      {isLoading ? renderLoading() : renderItens()}
    </Fragment>
  )
}

const spinnerStyle = {
  position: 'absolute',
  top: '10px',
  right: '50px',
}
